import React from 'react';
import styled from 'styled-components';

import {isDefined} from '../../../../../libs/shared/core/src/helpers/typescript/typeHelpers';
import {TeammateModel} from '../../../../../libs/shared/core/src/models/api/teammateModel';
import {buildTeammateDisplayName} from '../../../../../libs/shared/core/src/models/helpers/teammateModelHelpers';
import {ResourceMap} from '../../../../../libs/shared/core/src/store/commonAppTypes';
import {TeammateAvatar} from '../../../../../libs/shared/ui/src/components/avatar/teammateAvatar';
import {TooltipCoordinator} from '../../../../../libs/shared/ui/src/components/tooltips/tooltipCoordinator';
import {flexCenter} from '../../../../../libs/shared/ui/src/styles/mixins';
import {useUserTheme} from '../../../../../libs/shared/ui/src/theme/userThemeProvider';
import {ConversationModel} from '../../../../models/api/conversationModel';

const AVATAR_SIZE = 30;
const MAX_TEAMMATES = 3;

/*
 * Styles.
 */

const StyledTeammateAvatarWrapperDiv = styled.div`
  display: flex;
  margin-bottom: 8px;
`;

const StyledTeammateAvatarDiv = styled.div<{$zIndex: number}>`
  z-index: ${(p) => p.$zIndex};
  margin-right: -4px;
  background-color: ${(p) => p.theme.foreground};
`;

const StyledAvatarNumberDiv = styled.div`
  width: ${AVATAR_SIZE}px;
  height: ${AVATAR_SIZE}px;
  border-radius: 50%;
  ${flexCenter}
  justify-content: center;
  background-color: ${(p) => p.theme.greys.shade70};
  color: ${(p) => p.theme.greys.white};
`;

const StyledAssignedAvatarDiv = styled.div`
  margin-left: 16px;
`;

const StyledTeammatesNamesWrapperDiv = styled.div`
  display: flex;
  flex-direction: column;
`;

const StyledTeammateNameDiv = styled.div`
  padding: 1px 0px;
`;

/*
 * Props.
 */

interface ConversationDetailsHeaderTeammates {
  selectedConversation: ConversationModel;
  teammatesResourceMap: ResourceMap<TeammateModel>;
}

/*
 * Component.
 */

export const ConversationDetailsHeaderTeammates = ({
  selectedConversation,
  teammatesResourceMap,
}: ConversationDetailsHeaderTeammates) => {
  const theme = useUserTheme();
  const assignedTeammate = selectedConversation.assigneeId
    ? teammatesResourceMap[selectedConversation.assigneeId]
    : undefined;

  const teammates = Object.keys(selectedConversation.trackers)
    .map((trackerId) => (trackerId ? teammatesResourceMap[trackerId] : undefined))
    .filter(isDefined);

  const teammatesMaxLength = Math.min(teammates.length, MAX_TEAMMATES);

  // Avoid rendering two times the same teammate if there is only one teammate and this teammate is also assigned.
  if (teammates.length === 1 && teammates[0].id === assignedTeammate?.id) {
    return (
      <StyledTeammateAvatarWrapperDiv>
        {renderTeammateWithTooltipName(assignedTeammate, theme.palette.blue.shade40)}
      </StyledTeammateAvatarWrapperDiv>
    );
  }

  const individualTeammatesToRender = teammates.slice(0, teammatesMaxLength);
  const teammatesInTooltip = teammates.slice(teammatesMaxLength);

  return (
    <StyledTeammateAvatarWrapperDiv>
      {individualTeammatesToRender.map((teammate, index) => (
        <StyledTeammateAvatarDiv $zIndex={teammates.length - index} key={teammate.id}>
          {renderTeammateWithTooltipName(teammate, 'white')}
        </StyledTeammateAvatarDiv>
      ))}
      {maybeRenderTeammateCountOverflow(teammatesInTooltip)}
      {assignedTeammate && (
        <StyledAssignedAvatarDiv>
          {renderTeammateWithTooltipName(assignedTeammate, theme.palette.blue.shade40)}
        </StyledAssignedAvatarDiv>
      )}
    </StyledTeammateAvatarWrapperDiv>
  );
};

/*
 * Helpers.
 */

function renderTeammateWithTooltipName(teammate: TeammateModel, borderColor?: string) {
  return (
    <TooltipCoordinator render={() => buildTeammateDisplayName(teammate)}>
      <TeammateAvatar teammate={teammate} size={AVATAR_SIZE} borderColor={borderColor} />
    </TooltipCoordinator>
  );
}

function maybeRenderTeammateCountOverflow(overflowTeammates: ReadonlyArray<TeammateModel>) {
  const overflowTeammatesCount = overflowTeammates.length;
  if (overflowTeammatesCount < 1) {
    return null;
  }

  return (
    <TooltipCoordinator render={() => renderTeammatesNamesForTooltip(overflowTeammates)}>
      <StyledAvatarNumberDiv>{overflowTeammatesCount}</StyledAvatarNumberDiv>
    </TooltipCoordinator>
  );
}

function renderTeammatesNamesForTooltip(teammates: ReadonlyArray<TeammateModel>) {
  return (
    <StyledTeammatesNamesWrapperDiv>
      {teammates.map((teammate) => (
        <StyledTeammateNameDiv key={`teammate-${teammate.id}`}>
          {buildTeammateDisplayName(teammate)}
        </StyledTeammateNameDiv>
      ))}
    </StyledTeammatesNamesWrapperDiv>
  );
}
